export default {
  general: {
    title: 'General',
    description: 'We answer common questions about Kintell, from how it works to how you sign up.',
    image: require('~images/faq/general.png'),
    sections: [
      {
        id: '01.1',
        title: 'Signing up to Kintell',
        ref: 'signUp',
        questions: [
          {
            id: '01.1.1',
            q: 'Can anyone sign up to Kintell?',
            a: `Yup! It's free and silly easy to join Kintell. All it takes is a few details (<nuxt-link to="/join-now">here</nuxt-link>) and you’ll be learning from our Advisors in no time.<br>
          If you’ve got the knowledge and a yearning to become an Advisor yourself, you need to create a Kintell Card (see <a href="#becomeAnAdvisor">Becoming an Advisor</a>).`
          },
          {
            id: '01.1.2',
            q: 'What details do I need to provide when signing up?',
            a: 'We need your real name (so we know what to call you), your email (so we can get in touch) and your mobile number (so we can send you alerts). That\'s it! Or, if you’ve got a LinkedIn profile, you can sign up using that, too. Easy peasy.'
          },
          {
            id: '01.1.3',
            q: 'How much does it cost to sign up to Kintell?',
            a: 'Like all the best things in life, signing up to Kintell is free! You\'ll only need to pay when you book an Advisor for a Kintell session.'
          },
          {
            id: '01.1.4',
            q: 'Can I include my title in my name?',
            a: 'Kintell’s a community that’s all about merit and egalitarian knowledge sharing. So if you want to let people know about your formal education or any relevant certificates you’ve collected along the way, then you can include that in your Kintell Card description or as a skill. <nuxt-link to="/advisors/jane-qiu/international-business">Here\'s</nuxt-link> one we’ve prepared earlier.',
          },
        ]
      },
      {
        id: '01.2',
        title: 'Kintell Cards',
        ref: 'kintellCards',
        questions: [
          {
            id: '01.2.1',
            q: 'What is a Kintell Card?',
            a: 'A Kintell Card is your chance to show off your knowledge and experience and let others know what you’ve got the nous to advise on. Well, the first chance anyway – your next opportunity is with your Kintell sessions!'
          },
          {
            id: '01.2.2',
            q: 'Are Kintell Cards detectable by Google?',
            a: `By default, yes. Standard Kintell Cards are listed publicly on the Kintell.com site, which means Google will undoubtedly pick them up sooner or later.<br>
If you’d prefer to have your Cards only seen by those who you’ve sent a link to, then a Private Kintell Card is the right choice for you.`
          },
          {
            id: '01.2.3',
            q: 'What is a Private Kintell Card?',
            a: 'They’re the ‘incognito mode’ of Kintell Cards ;) Private Kintell Cards aren\'t searchable, and don\'t have ratings and reviews. They also have a lower admin fee (see <nuxt-link to="/pricing">Pricing</nuxt-link> for details), so they’re perfect for ongoing meetings with new or old clients. If you create a free Private Card, you can hold meetings through Kintell and take full advantage of the integrated calendar and booking system, without paying a penny.'
          }
        ]
      },
      {
        id: '01.3',
        title: 'Bookings',
        ref: 'bookings',
        questions: [
          {
            id: '01.3.1',
            q: 'What is a booking?',
            a: 'A booking is simply a video meeting or session between two people via Kintell’s integrated video chat.',
          },
          {
            id: '01.3.2',
            q: 'Do I need to be signed up to Kintell to make a booking?',
            a: 'We want to make sure that Advisors only receive valid booking requests from actual real people and not three dogs in an overcoat, so you\'ll have to sign up to Kintell when making a booking. Luckily it’s as easy as 1-2-3!'
          },
          {
            id: '01.3.3',
            q: 'Can I integrate my calendar with Kintell?',
            a: 'But of course! Google, Outlook and any other digital calendar are easily synced. Plus we’ll block out those entries in your <nuxt-link to="/my-kintell/my-calendar">Kintell calendar</nuxt-link>, so you can be sure you won’t get double-booked.'
          },
          {
            id: '01.3.4',
            q: 'What is an international booking?',
            a: 'Any Kintell session that involves users in different countries is considered as an international booking – pretty much what it says on the tin, here!',
          },
          {
            id: '01.3.5',
            q: 'What countries are supported internationally?',
            a: 'Anyone in the world – yes, anyone – with a valid credit/debit card can make a booking with advisors from Australia, New Zealand, the UK, Sweden and Denmark. We\'re working around the clock to support more countries, but for now, Advisors from other countries can only take free bookings.',
          },
        ]
      },
      {
        title: 'Video Sessions',
        ref: 'videoSessions',
        id: '01.4',
        questions: [
          {
            id: '01.4.1',
            q: 'Do I need any special equipment?',
            a: 'All of our Kintell video sessions are held on Kintell.com, or via the iPhone Kintell Companion App (available at the <a href="https://apps.apple.com/ca/app/kintell/id1419104805">App Store</a>), and you can use any device you like. If you’re using your laptop or desktop computer, you just need to be sure you can use your camera and microphone (pretty important if you’re doing a video chat, I reckon!). We recommend throwing on your headphones to block out surrounding noises :)',
          },
          {
            id: '01.4.2',
            q: 'What if my device is too old?',
            a: 'Some older devices (usually tablets or phones over 5 years old) can\'t process the video in real-time, so you might not get the best video experience. Sorry! If you\'re having problems with the device you\'re using, jump onto another device and rejoin the booking so you can continue your session and keep on learning.'
          },
          {
            id: '01.4.3',
            q: 'What browser or operating system works best with Kintell?',
            a: 'Google Chrome – whatever the latest version is – installed on Mac or Windows. Other browsers work as well, but Chrome is the most stable and is going to give you the best Kintell experience.',
          },
          {
            id: '01.4.4',
            q: 'Will I be able to test my setup?',
            a: 'Of course! Before each booking, you\'ll have the chance to check if your camera and microphone are up to speed so you don\'t waste any of your session on set up when you could be getting insights from a champion of industry.'
          },
          {
            id: '01.4.5',
            q: 'What happens if I have a poor internet connection?',
            a: 'You’ll see an icon indicating your network quality level (the familiar signal bars). Besides, we’ve designed the Kintell video chat to prioritise audio over video, so even if you get knocked a step closer to dial-up, your conversation should go on uninterrupted.\n'
          },
          {
            id: '01.4.6',
            q: 'Can I send text messages or files?',
            a: 'Good question! Yes indeed – we\'ve included a text chat in your Kintell session and on your booking page so that you can send messages, links and files before, during and after a booking!',
          },
          {
            id: '01.4.7',
            q: 'Can I share my screen?',
            a: 'If you’re on a laptop or desktop, then absolutely. Share to your heart’s content! If you’re on the Kintell Companion App on your mobile device, then you won’t be able to share your screen – but you will be able to see the other party\'s screen if they share it.',
          },
          {
            id: '01.4.8',
            q: 'What happens if I take the session off the platform?',
            a: 'If you and your booking decide to go into No-Man’s Land and not use Kintell’s video chat, then you’ll be logged as a “No Show”. It might interfere with your booking payment – but you’ll also get no rating, no review, your Kintell rating will be affected, and perhaps most seriously, you’ll break our hearts! 💔.'
          }
        ]
      },
      {
        id: '01.5',
        title: 'Ratings & Reviews',
        ref: 'ratingsReviews',
        questions: [
          {
            id: '01.5.1',
            q: 'What impact do reviews have?',
            a: 'Giving a great review means that you’ve gained a lot from your session. So Kintell Cards with good reviews get higher visibility on the platform, and among the search results. That means more opportunities to connect, and more chances to showcase your knowledge and ask the right questions!'
          },
          {
            id: '01.5.2',
            q: 'Are there other factors that can impact Advisors’ ratings?',
            a: 'It’s not just ratings, it’s true. We also take into account people’s behaviour on the platform. It won’t do your account visibility any favours if you frequently cancel bookings, let booking requests expire, or don’t show up for Kintell sessions. Check out our <nuxt-link to="/code-of-conduct">Code of Conduct</nuxt-link> for more info on how to be the best kind of Kintell user.\n'
          },
          {
            id: '01.5.3',
            q: 'When can I rate and review an Advisor?',
            a: 'Once you\'ve finished a Kintell session, we\'ll ask you to leave a review. And your ratings matter! Active, quality members are the engine of our community, and rating and reviews are essential to maintaining high quality and trust. Reviews mean that you’re doing your part to ensuring Kintell is a great platform for all.'
          },
          {
            id: '01.5.4',
            q: 'Do Advisors review Learners as well?',
            a: 'For sure – reviews are a two-way street. Learners\' ratings and reviews aren’t public, though; when a Learner sends a booking request, the Advisor will see their rating and have a chance to see what kind of Learner they’ve been.'
          },
          {
            id: '01.5.5',
            q: 'Do I have to leave a review?',
            a: 'Yes! Reviews mean that you\'re able to make a better-informed decision when booking an Advisor. That’s why we’ve made it mandatory to leave a review before you can do anything else on the platform. Thanks for making a difference :)\n'
          }
        ],
      },
      {
        id: '01.6',
        title: 'Payment & Fees',
        ref: 'paymentFees',
        questions: [
          {
            id: '01.6.1',
            q: 'When do I pay for a booking?',
            a: 'After you’ve made a booking, we’ll ask for your payment details. We accept all the major credit and debit cards, and you\'ll only be charged once the Advisor has confirmed the booking.',
          },
          {
            id: '01.6.2',
            q: 'Will I get a receipt?',
            a: 'Certainly! We\'ll send a summary and receipt once you\'ve completed a booking.',
          },
          {
            id: '01.6.3',
            q: 'Do you store my card details?',
            a: 'Nope! We use Stripe’s encrypted and secure payment gateway to handle your details, and we don’t store that info on our servers at all.'
          },
          {
            id: '01.6.4',
            q: 'Is there an admin fee?',
            a: `For all paid bookings, there’s an admin fee ranging from 3% to 15% that helps us keep our servers running and Advisors connected with Learners.<br>
            If a person’s Card is free, or if someone is booking you with their <a href="#invitesRewards">Pro Bono Credit</a>, there's no admin fee. Pretty easy! Head over to our <nuxt-link to="/pricing">Pricing</nuxt-link> page to find out more.`
          },
          {
            id: '01.6.5',
            q: 'Will these fees change?',
            a: 'If they do change, then you’ll be the first to know. You can always check the <nuxt-link to="/pricing">Pricing</nuxt-link> page for updates!'
          }

        ]
      },
      {
        id: '01.7',
        title: 'Invites & Rewards',
        ref: 'invitesRewards',
        questions: [
          {
            id: '01.7.1',
            q: 'What\'s the difference between being invited and signing up on the website?',
            a: 'Well, if you\'re invited to Kintell by a member, the sign-up process is a little bit simpler and quicker. And your inviter will earn Pro Bono Credit if you sign up using their invite link. It’s the same if you invite someone – you’ll earn some Pro Bono Credit too!',
          },
          {
            id: '01.7.2',
            q: 'What are Pro Bono Credit Rewards?',
            a: 'Pro Bono basically means ‘free’ or ‘voluntary’. By inviting your friends and colleagues to Kintell, you can earn Pro Bono Credit to spend on Kintell sessions. It\'s a great way to get the credit that you can use on the platform while helping us grow and improve the community. Check out <nuxt-link to="/my-kintell/my-rewards">Invites & Rewards</nuxt-link> for more details :)'
          },
          {
            id: '01.7.3',
            q: 'How do I send out invites?',
            a: 'It\'s so easy even your grandma can do it! Pop in the person’s name and email to your <nuxt-link to="/my-kintell/my-rewards">Invites & Rewards</nuxt-link> page, and we\'ll do the rest. From there, you can track all your invites and their statuses. You\'ll get 10 minutes of Pro Bono credit when invitees sign up, and 30 minutes if they become an Advisor. Cheers!'
          },
          {
            id: '01.7.4',
            q: 'How do I use my Pro Bono Credit?',
            a: 'The only thing better than getting is giving, which is why we encourage all of our Advisors to provide some of their time for Pro Bono bookings each month and give back to the community. When booking an advisor, you’ll be able to see if you can use your Pro Bono Credit for the session.'
          },
          {
            id: '01.7.5',
            q: 'How do I track my Credits?',
            a: 'On your Credits page, you can see your current Pro Bono Credit, as well as other Kintell Credit you might have accumulated. Too easy!'
          }
        ]
      },
    ],
  },
  learner: {
    title: 'For learners',
    description: 'Looking to book an advisor? We outline the key issues for learners on Kintell.',
    image: require('~images/faq/learners.png'),
    sections: [
      {
        id: '02.1',
        title: 'Making a booking',
        ref: 'makingABooking',
        questions: [
          {
            id: '02.1.1',
            q: 'How do I make a booking with an Advisor?',
            a: 'Brace yourself, because this can get complicated: When you\'ve found the right Advisor (yay!) you can book them through their Kintell Card by clicking \'Book now\'. Did we say complicated? We meant easy! We\'ve designed a simple process for you to suggest times and duration for the booking, and set up the payment.',
          },
          {
            id: '0.2.1.2',
            q: 'How much does a booking cost?',
            a: 'Advisors decide their fees per half hour. Check out our <nuxt-link to="/pricing">Pricing</nuxt-link> page for more details.'
          },
          {
            id: '0.2.1.3',
            q: 'What can I expect after I\'ve sent a booking request?',
            a: 'The Advisor will check out your suggested times and select one of them – or suggest alternative times if the ones you’ve offered don’t quite fit the suit. And if the Advisor cancels the booking, you won\'t be charged and the money will stay in your back pocket. Check out our <nuxt-link to="/cancellation-policy">Cancellation Policy</nuxt-link> for more details.'
          },
          {
            id: '0.2.1.4',
            q: 'How will I know if my booking was accepted?',
            a: 'We keep the comms coming, so we\'ll send you email and SMS notifications during each phase of the booking process. You\'ll always know what\'s going on. We\'ll also pop the booking into your Dashboard under <nuxt-link to="/my-kintell/bookings">Bookings</nuxt-link>.'
          },
          {
            id: '0.2.1.5',
            q: 'What happens if my booking request expires?',
            a: 'If an Advisor doesn\'t respond to your booking request within the expiry time, the booking will be cancelled, you won\'t be charged, and the Advisor\'s ratings will be affected. So if you’re an Advisor, it pays to keep an eye on your bookings!'
          },
          {
            id: '0.2.1.6',
            q: 'How do I secure a booking?',
            a: 'Our most popular Advisors are busy people, so when placing a booking request, try to give as many time ranges as possible, and preferably a few days’ notice! This will increase the chances of striking a time that’ll be golden for you both.',
          }
        ]
      },
      {
        id: '02.2',
        title: 'Rescheduling or cancelling a booking',
        ref: 'reschedulingOrCancellingABooking',
        questions: [
          {
            id: '02.2.1',
            q: 'How does rescheduling work?',
            a: 'That’s easy! You can always request to reschedule a confirmed booking, and you’ll be able to message the Advisor beforehand to let them know. If the Advisor can\'t make the new times, they\'ll cancel the booking, and you\'ll only pay the admin fee for paid bookings. But – and it’s a big but here – if you ask to reschedule within 24 hours of the Kintell session then you might notice a hit to your Kintell rating; if it\'s a paid booking, you\'ll pay a fee to the Advisor for their trouble. Check out our <nuxt-link to="/cancellation-policy">Cancellation Policy</nuxt-link> to learn more.',
          },
          {
            id: '02.2.2',
            q: 'What if I need to cancel my booking?',
            a: 'Life happens, and you can always cancel a confirmed booking. You’ll be able to message the Advisor beforehand and let them know, too. However, if you cancel within 24 hours of the video session, it will harm your Kintell rating. Sorry! You\'ll also have to square up the admin fee, plus a fee to the booked Advisor. We want to encourage our users only to confirm bookings that they\'re able to carry out. Check out our <nuxt-link to="/cancellation-policy">Cancellation Policy</nuxt-link> to learn more.',
          },
          {
            id: '02.2.3',
            q: 'What happens if an Advisor wants to reschedule?',
            a: 'The Advisor will send over new times for you to confirm. If they don\'t work for you, you can always cancel the booking and focus on finding another Advisor. If you don\'t respond to the request, the booking will be cancelled – and it may harm your Kintell rating :(',
          },
          {
            id: '02.2.4',
            q: 'What if an Advisor cancels my booking?',
            a: 'We encourage Advisors not to do this. However, sometimes life gets in the way, and we want to do our bit to keep things rolling. If your Kintell session is cancelled, you\'ll get a full refund (if it was a paid booking) and as much notice as possible.',
          },
          {
            id: '02.2.5',
            q: 'How do refunds work?',
            a: 'If you\'re entitled to a refund, then we’ll get onto it and it’ll be paid to your account within a few days.'
          }
        ]
      },
      {
        id: '02.3',
        title: 'Having a booking',
        ref: 'havingABooking',
        questions: [
          {
            id: '02.3.1',
            q: 'How do I start my booking?',
            a: 'Don\'t worry – when your booking is coming up, we\'ll send you a notification from which you can click through and join the session. You can also follow the prompts in the <nuxt-link to="/my-kintell/bookings">Booking</nuxt-link> section of your Dashboard :)'
          },
          {
            id: '02.3.2',
            q: 'What if I miss my Kintell booking?',
            a: 'First of all, oops! Secondly, if you fail to show up for your video session, it will harm your Kintell rating, sorry. And you\'ll have to pay the full fee for the booking. This is to make sure our users only confirm bookings that they\'re able to carry out. Check out our <nuxt-link to="/cancellation-policy">Cancellation Policy</nuxt-link> to learn more.',
          },
          {
            id: '02.3.3',
            q: 'What if the Advisor misses the Kintell booking?',
            a: 'If the Advisor doesn\'t join the video session, you\'ll get a full refund on your booking fees. Just give it a couple of days to get back into your account.'
          },
          {
            id: '02.3.4',
            q: 'What if I want to end the call early?',
            a: 'You can end the call whenever you like - but keep in mind that you\'ll be charged for the time you booked out. However, if you feel as though the first session isn\'t what you were expecting, you can end the video meeting within the first 5 minutes – free of charge!\n'
          },
          {
            id: '02.3.5',
            q: 'What if the call goes over the booked time?',
            a: 'It’s great if you’re vibing! If a session goes on longer than expected, it\'s up to you and the Advisor whether you should continue.'
          }
        ]
      }
    ]
  },
  advisor: {
    title: 'For advisors',
    description: 'If you’re looking to sign up as an advisor, or have queries about how it works, this is for you.',
    image: require('~images/faq/advisors.png'),
    sections: [
      {
        id: '03.1',
        title: 'Becoming an Advisor',
        ref: 'becomeAnAdvisor',
        questions: [
          {
            id: '03.1.1',
            q: 'How do I become a Kintell Advisor?',
            a: 'Once you\'ve signed up to Kintell, you can create your first <a href="#kintellCards">Kintell Card</a> to show off your expertise, you clever thing you. You can create a Private Card, share it with whomever special you want, and start advising straight away. If you want to create a Standard Kintell Card that’s listed on the platform, the Kintell team will review the Card; once it\'s approved you can start taking bookings. Didn’t we say it was easy peasy? Check out <nuxt-link to="/my-kintell/kintell-cards">Become an Advisor</nuxt-link> to learn more :)\n'
          },
          {
            id: '03.1.2',
            q: 'Why do advisors need a photo?',
            a: 'Kintell is about real people, and profile pictures are essential. A picture puts a face to a name and verifies that you are genuine to other users - and lets them know that they’ve got the right person when you jump onto your Session.',
          },
          {
            id: '03.1.3',
            q: 'What can I advise on?',
            a: 'You can advise on any topic you\'d like, as long as it doesn\'t break our <nuxt-link to="/code-of-conduct">Code of Conduct</nuxt-link>, and that you\'re confident that you can provide sound advice to our community. We expect you to be professional, regardless of the topic, and a credit to your knowledge.'
          },
          {
            id: '03.1.4',
            q: 'When can I take bookings?',
            a: 'You decide when you can take bookings so you can customise your working hours and make sure it fits your schedule. Make sure your <nuxt-link to="/my-kintell/my-calendar">calendar</nuxt-link> is up to date so that our users book you when you\'ve got the time. That will increase the chances of settling on a time that suits you both and making some magic happen.\n'
          },
          {
            id: '03.1.5',
            q: 'Is there a minimum amount of time I have to commit?',
            a: 'Nope! You decide when to take bookings and how much time you want to put in and can even set your <nuxt-link to="/my-kintell/my-calendar/working-hours">Working hours</nuxt-link> to control your availability.'
          }
        ]
      },
      {
        id: '03.2',
        title: 'Creating a Kintell Card',
        ref: 'creatingAKintellCard',
        questions: [
          {
            id: '03.2.1',
            q: 'What\'s considered a \'good\' Kintell Card?',
            a: 'A clearly defined topic with distinct skill tags, subtopics to show what you can advise on, and a well-written overview of your knowledge and experience will make a banger of a Card. Consider adding your LinkedIn to give weight to your Kintell Card, and if you have supporting links such as websites or articles, pop them in too! Check out <nuxt-link to="/jane-qiu/international-business">this example</nuxt-link> for inspiration :)',
          },
          {
            id: '03.2.2',
            q: 'Why does my Card need to be approved?',
            a: 'We review the first Card of a new member to keep quality high and make sure you’re going to add to the shared knowledge of our users. It\'s our way of keeping the community trustworthy and safe, but also to help you create a great Kintell Card that will generate some amazing bookings.'
          },
          {
            id: '03.2.3',
            q: 'How many Kintell Cards can I have?',
            a: 'You can have as many Kintell Cards as you like, on as many topics as you want, as long as you believe you can provide useful advice on the subject. For example, a languages teacher may want to post one Card on \'English Speaking & Writing\' and another one on \'Bonsai Gardening\', if those are their specialties. As long as you’re passionate and knowledgeable about the subject, go for gold!',
          },
          {
            id: '03.2.4',
            q: 'How do I share my Kintell Cards with others?',
            a: 'In the <nuxt-link to="/my-kintell/kintell-cards">\'Kintell Cards\'</nuxt-link> section of your Dashboard, each Card will have a share icon at the bottom from which you can copy the link to your Card, and share with whomever you want!',
          }
        ]
      },
      {
        id: '03.3',
        title: 'Promoting your Kintell Profile',
        ref: 'promotingYourKintellProfile',
        questions: [
          {
            id: '03.3.1',
            q: 'How do I use different types of Kintell Cards?',
            a: 'You can create multiple Kintell Cards for specific purposes. You might have a free introductory Card to accumulate ratings & reviews, and a Card with your standard offeringIf you have a follow-up session, you might want to use a Private Kintell Card. For paid sessions on a Private Card, there\'s only a 3-5 % admin fee.',
          },
          {
            id: '03.3.2',
            q: 'How can I make my Kintell Card more bookable?',
            a: 'Make sure your social media links, especially LinkedIn, are up to date to verify that you are who you say you are. When writing your Kintell Card, make sure to have relevant and popular keywords (lawyer, digital marketing, accountant, etc.). It\'s through the keywords that people will find you! And make sure that you and your personality really shine ⭐',
          },
          {
            id: '03.3.3',
            q: 'How do I accumulate ratings and reviews?',
            a: 'A good idea to build your profile is to set the fee of your listed Kintell Card lower at the beginning. This will help generate your early ratings and reviews – which are needed for other users to book you. Our algorithms favour Cards with more reviews, so when you\'ve acquired a few, you can raise your fee and charge more for your skilled and valued time :) You can also share your standard Kintell Card with existing clients for follow-up chats, and build your reviews on the platform that way.',
          },
          {
            id: '03.3.4',
            q: 'How do I spread the word on social media?',
            a: 'Sharing your Kintell Cards on your social networks is a great way to bring users to your profile – and get sessions and reviews heading your way, of course! Share it on LinkedIn to reach a professional audience, or share your latest review from a Kintell learner on Instagram and Facebook to spread the word about your offer – and the love!',
          },
          {
            id: '03.3.5',
            q: 'What else can I do to get the word out?',
            a: 'Do you give workshops or facilitate meetups? Make sure you give out the URL of your Kintell Cards to offer them a one-to-one follow-up session. Met a new potential client who\'s offered you a coffee to "pick your brain"? Ask them to book you through Kintell! When signing up, they\'ll receive a Kintell Credit amount depending on where they are (for example, if they’re signing up from the USA, they’ll get $15USD) to use for the booking. You\'ll get paid your full rate, and accumulate ratings and reviews to build your global reputation as an advisor. Go you!\n'
          }
        ]
      },
      {
        id: '03.4',
        title: 'Receiving a booking request',
        ref: 'receivingABookingRequest',
        questions: [
          {
            id: '03.4.1',
            q: 'How does a booking request work?',
            a: 'The Learner will suggest some times from your available slots. If one of them works for you, then just select it and confirm your booking! If the time slots don\'t work, you can either reschedule the booking by suggesting other times, or cancel it. The quicker you can respond, the better – since that way things keep moving and shaking and you don’t disappoint. You can find out more in the Kintell <nuxt-link to="/code-of-conduct">Code of Conduct</nuxt-link>.',
          },
          {
            id: '03.4.2',
            q: 'What if I let a booking request expire?',
            a: 'The Kintell listings have been designed to favour Advisors who respond to booking requests quickly. If you do let a booking request expire, it’ll harm your Kintell rating and visibility on the platform – so make sure you’re keeping on top of them! If you don’t respond to booking requests where you\'ve had 48 hours or more to respond, we may temporarily unpublish your Kintell Card from the platform until you’re ready to respond to bookings again. We’ve got to make sure that our users only book Advisors that respond to their requests, after all. No use having a Card if you’re not going to share your knowledge!',
          },
          {
            id: '03.4.3',
            q: 'How do I secure a booking?',
            a: 'Make sure your <nuxt-link to="/my-kintell/my-calendar">Calendar</nuxt-link> is up to date so that our users can book you when you\'ve got the time. This will increase the chances of settling on a time that suits you both :)\n'
          },
          {
            id: '03.4.4',
            q: 'Can I initiate a booking as an advisor?',
            a: 'You sure can! Under <nuxt-link to="/my-kintell/bookings/initiate">\'Initiate a Booking\'</nuxt-link> in your Dashboard, you can send an invitation for a Kintell session via email, or by getting a link to the booking that you can share with whomever you want :)',
          }
        ]
      },
      {
        id: '03.5',
        title: 'Rescheduling or cancelling a booking',
        ref: 'reschedulingOrCancellingABooking',
        questions: [
          {
            id: '03.5.1',
            q: 'What if I need to reschedule my bookings?',
            a: 'You can always request to reschedule a confirmed booking. Ain’t that grand! If the Learner can\'t make the new times, they\'ll cancel the booking. After all, it’s a two-way street :) However, if you ask to reschedule within 24 hours of the Kintell session starting, it will harm your Kintell rating. Check out our <nuxt-link to="/cancellation-policy">Cancellation Policy</nuxt-link> to learn more. \n'
          },
          {
            id: '03.5.2',
            q: 'What if I need to cancel my booking?',
            a: 'You can always cancel a confirmed booking. However, if you cancel within 24 hours of the video session, it’ll harm your Kintell rating and reduce the visibility of your Kintell Card on the platform. We’ve got to encourage our users only to confirm bookings that they\'re able to carry out, after all! \n',
          },
          {
            id: '03.5.3',
            q: 'What happens if a Learner wants to reschedule?',
            a: 'The Learner will flick through new times for you to confirm. If they don\'t work for you, you can cancel the booking. It’s not set in stone, after all! If the request expires, the booking will be cancelled, and – FYI – it may harm your Kintell rating. The quicker you can respond, the better - you can find out more in the Kintell <nuxt-link to="/cancellation-policy">Code of Conduct</nuxt-link>.',
          },
          {
            id: '03.5.4',
            q: 'What if a Learner cancels my booking?',
            a: 'When a Learner cancels a booked session, you won\'t receive any payment. Sorry! However, if a Learner cancels a confirmed booking within 24 hours of the video session, you\'ll get paid a part of your rate for the inconvenience (if it was a paid booking). It’s a little sweetener for a less-than-ideal situation.',
          },
        ]
      },
      {
        id: '03.6',
        title: 'Holding a booking',
        ref: 'holdingABooking',
        questions: [
          {
            id: '03.6.1',
            q: 'How do I start my booking?',
            a: 'Don\'t worry – when your booking is coming up, we\'ll send you a notification from which you can click through and join the session. You can also follow the prompts in the <nuxt-link to="/my-kintell/bookings">Booking</nuxt-link> section of your Dashboard :)\n'
          },
          {
            id: '03.6.2',
            q: 'What if I miss my booking?',
            a: 'First of all, oops! Secondly, if you fail to show up for your video session, it will harm your Kintell rating, sorry. If you\'ve missed Kintell sessions, we’ll unpublish your Kintell Cards temporarily or potentially permanently. After all, it’s no use having Cards if you’re not going to use them! The Kintell team may get in touch to get a better understanding of the situation and square things up between everyone. This is to make sure our users only confirm bookings that they\'re able to carry out. Check out our <nuxt-link to="/cancellation-policy">Cancellation Policy</nuxt-link> to learn more.\n'
          },
          {
            id: '03.6.3',
            q: 'What if the Learner misses the Kintell booking?',
            a: 'If the Learner doesn\'t join the video session, you\'ll still get paid for the booking – at least it’s not a total loss! We want to make sure our users only confirm bookings that they\'re able to carry out. Check out our <nuxt-link to="/code-of-conduct">Code of Conduct</nuxt-link> to learn more.'
          },
          {
            id: '03.6.4',
            q: 'What if the call goes over the booked time?',
            a: 'It’s great that you’re really firing together! If a session goes on longer than expected, it\'s up to you and the Learner whether you should continue.'
          }
        ]
      },
      {
        id: '03.7',
        title: 'Receiving payments',
        ref: 'receivingPayments',
        questions: [
          {
            id: '03.7.1',
            q: 'How will I get paid?',
            a: 'When you receive your first booking request as an Advisor, we\'ll ask you for your bank details so that we can pay you. For a bit of Ts & Cs, it’s important to know that, due to financial regulations, our payment processing partner Stripe has to ask for your date of birth so you can be verified as a real person (more information <a href="https://support.stripe.com/questions/know-your-customer-obligations">here</a>). You can update your payment details at any time on your <nuxt-link to="/my-kintell/my-details/billing">\'Billing\'</nuxt-link> page.',
          },
          {
            id: '03.7.2',
            q: 'How long does it take to set up?',
            a: 'Less time than a lamb’s tail shake. Putting your details in is easy, and after that it usually takes a day or so before your account details are verified. Stripe, our payment processing partner, will be in touch if they need further information and to confirm you\'re valid. It’s super easy!',
          },
          {
            id: '03.7.3',
            q: 'When do I get paid?',
            a: 'After your booking is complete, you\'ll have the money in your account within a week, if you\'ve provided your account details. Now you’re talking!',
          }
        ]
      },
    ]
  }
}
